import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  BookingTariff,
  SetBookingContingentReservation,
  SetProductBookingContingentReservation
} from '@products/models/booking.model';
import { RemoveContingentReservation } from '@products/models/contingent.model';
import { getProductType } from '@products/models/products.model';
import { Tariff } from '@products/models/tariff.model';
import { ProductBookingService } from '@products/services/product-booking.service';
import { VoucherService } from '@products/services/voucher.service';
import { convertStringToDate } from '@shared/app-utils';
import { getAllBookedContingentTariffs } from '@store/products/booking/product/contingent/contingent-booking.selectors';
import { getAllTariffs } from '@store/products/product-selection/product-selection.selectors';
import { Observable, combineLatest, first, forkJoin, map, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ProductService {
  private store = inject(Store);
  private productBookingService = inject(ProductBookingService);
  private voucherService = inject(VoucherService);

  getSortedBookingContingentReservationsByProductSelectionListOrder$(
    removeContingentReservations: RemoveContingentReservation[]
  ): Observable<SetProductBookingContingentReservation[]> {
    return combineLatest([this.store.select(getAllTariffs), this.store.select(getAllBookedContingentTariffs)]).pipe(
      first(([allTariffs, allBookedContingentTariffs]) => !!allTariffs.length && !!allBookedContingentTariffs),
      map(([productSelectionTariffs, bookingContingentTariffs]) => {
        const sortedSetProductBookingContingentReservationsByProductSelectionListOrder: SetProductBookingContingentReservation[] =
          [];

        productSelectionTariffs.forEach((tariff: Tariff) => {
          const { ticketTypeId, ticketPersonId, ticketPersonTypeId, voucherCode, packageNumber, packageIndex } = tariff;

          const bookedTariff: BookingTariff = bookingContingentTariffs.find(
            (bookedTariff) =>
              bookedTariff.ticketTypeId === ticketTypeId &&
              bookedTariff.ticketPersonId === ticketPersonId &&
              bookedTariff.voucherCode === voucherCode &&
              bookedTariff.packageNumber === packageNumber &&
              bookedTariff.packageIndex === packageIndex
          );

          if (bookedTariff) {
            const removeCurrentBookingTariffContingentReservation = removeContingentReservations.find(
              (removeContingentReservation) =>
                removeContingentReservation.ticketTypeId === ticketTypeId &&
                removeContingentReservation.ticketPersonId === ticketPersonId &&
                removeContingentReservation.voucherCode === voucherCode &&
                removeContingentReservation.packageNumber === packageNumber &&
                removeContingentReservation.packageIndex === packageIndex
            );

            const setProductBookingContingentReservation: SetProductBookingContingentReservation = {
              productType: getProductType(bookedTariff),
              ticketTypeId,
              ticketPersonId,
              ticketPersonTypeId,
              voucherCode,
              packageNumber,
              packageIndex,
              contingentReservations: []
            };

            bookedTariff.contingentReservations.forEach((contingentReservation, bookingReservationIndex) => {
              if (
                removeCurrentBookingTariffContingentReservation &&
                removeCurrentBookingTariffContingentReservation.removeBookingReservationIndexes.includes(
                  bookingReservationIndex
                )
              ) {
                return;
              }

              const setBookingContingentReservation: SetBookingContingentReservation = {
                ...contingentReservation,
                duration: tariff.durationInDays,
                toDate: convertStringToDate(tariff.validTill),
                bookingReservationIndex
              };

              setProductBookingContingentReservation.contingentReservations.push(setBookingContingentReservation);
            });

            sortedSetProductBookingContingentReservationsByProductSelectionListOrder.push(
              setProductBookingContingentReservation
            );
          }
        });

        return sortedSetProductBookingContingentReservationsByProductSelectionListOrder;
      })
    );
  }

  releaseAllVouchersAndProductBookings() {
    return this.productBookingService
      .removeAllProductBookings$()
      .pipe(
        first(),
        switchMap(({ removePostProductBookings }) => {
          return forkJoin([
            this.productBookingService.postProductBookings$(removePostProductBookings),
            this.voucherService.releaseAllVouchers$()
          ]);
        })
      )
      .subscribe();
  }
}
